import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { CAMPO } from "./ui";
import { MapaDoBrasilSvg, type DegrauDoMapa } from "./MapaUi";
import {
  STATUS,
  corDaRegiao,
  corDoStatus,
  corNoModoTrf,
  marcadosDo,
  nomeNaLista,
  rotuloDoStatus,
  statusDe,
  type Catalogo,
  type Documento,
  type Progresso,
  type StatusDoCadastro,
  type TipoDeTribunal,
  type Tribunal,
} from "../lib/forofacil";

// As peças do ForoFácil. Só apresentação: nenhuma decide acesso, nenhuma busca
// dado, nenhuma grava. Quem grava é `paginas/ForoFacil.tsx`, pelas RPCs.
//
// ---------------------------------------------------------------------------
// O mapa pinta o STATUS do aluno, e é a primeira vez que este mapa faz isso
// ---------------------------------------------------------------------------
// Na origem `BrazilMap` recebia `progress` e não usava: a cor era a REGIÃO (TJ)
// ou o TRF — decoração. O aluno com doze cadastros concluídos via o mesmo mapa
// de quem acabou de entrar. Aqui cada estado é pintado pelo status do cadastro
// do aluno naquele tribunal; no modo TRF, pelo status do TRF a que a UF
// pertence — assim o Paraná, Santa Catarina e o Rio Grande do Sul acendem
// juntos quando o TRF-4 é concluído.
//
// As três cores são TOKEN da escala do coroplético (`--cf-mapa-*`), medidas em
// 27/08 contra o navy: o zero recua, o meio é azul claro, o topo é laranja — dois
// matizes, porque luminância sozinha não separa "em andamento" de "concluído".
// Dentro de `painel-navy` o fundo é escuro nos dois temas, então os tokens são
// fixos e a igualdade entre os temas é o esperado.
//
// No modo TRF há uma camada A MAIS, e ela nasceu de um defeito visto na tela em
// 03/09: como o degrau zero RECUA e ninguém tem cadastro no primeiro dia, o mapa
// dos federais saía como uma mancha única — *"os estados do TRF não ficaram
// pintados"*. Ali a região é o FUNDO (seis matizes escolhidos por medição de ΔE,
// em `corDaRegiao`) e o status pinta POR CIMA quando existe. As duas informações
// cabem no mesmo mapa porque nenhum tom de região chega perto das duas cores de
// status — a menor distância medida é ΔE 41,6.

/** A escala de status, para a `LegendaDaEscala` genérica de `MapaUi`. */
export const ESCALA_DE_STATUS: DegrauDoMapa[] = [
  { ate: 0, token: corDoStatus("nao_iniciado"), rotulo: "não iniciado" },
  { ate: 1, token: corDoStatus("em_andamento"), rotulo: "em andamento" },
  { ate: 2, token: corDoStatus("concluido"), rotulo: "concluído" },
];

/**
 * A legenda do modo TRF: as seis regiões, e só DEPOIS os dois estados de
 * progresso — nesta ordem porque é assim que o mapa se lê. A região é o fundo;
 * o status pinta por cima quando existe. O porquê dos matizes (e a medição em
 * ΔE que os escolheu) está em `corDaRegiao`.
 */
export function escalaDeRegioes(trfs: Tribunal[]): DegrauDoMapa[] {
  return [
    ...trfs.map((t, i) => ({ ate: i, token: corDaRegiao(t.sigla), rotulo: t.nome })),
    { ate: trfs.length, token: corDoStatus("em_andamento"), rotulo: "em andamento" },
    { ate: trfs.length + 1, token: corDoStatus("concluido"), rotulo: "concluído" },
  ];
}

/**
 * O mapa das 27 UFs pintado pelo status do aluno — um invólucro de
 * `MapaDoBrasilSvg` (a geometria, as 8 chamadas e o `aria-hidden` moram lá).
 *
 * `modo = 'trf'` pinta e ABRE o TRF da UF clicada: o mapa continua sendo o dos
 * estados, porque TRF não tem contorno — é o desenho da origem, e é o certo.
 */
export function MapaDeTribunais({
  catalogo,
  progresso,
  modo,
  selecionado,
  onEscolher,
}: {
  catalogo: Catalogo;
  progresso: Progresso | null;
  modo: TipoDeTribunal;
  /** A sigla do tribunal aberto no painel ao lado, quando há um. */
  selecionado?: string | null;
  onEscolher: (sigla: string) => void;
}) {
  const tjs = catalogo.tribunais.filter((t) => t.tipo === "tj");
  const porUf = new Map(tjs.map((t) => [t.sigla, t]));
  const porSigla = new Map(catalogo.tribunais.map((t) => [t.sigla, t]));
  const alvoDaUf = (uf: string): Tribunal | undefined => {
    const tj = porUf.get(uf);
    if (!tj) return undefined;
    return modo === "trf" ? porSigla.get(tj.trf ?? "") : tj;
  };
  // Os ESTADOS que o tribunal selecionado ocupa. Num TJ é um; num TRF é a
  // jurisdição inteira, e é por isso que o mapa recebe uma lista.
  const ufsSelecionadas = tjs.filter((t) => alvoDaUf(t.sigla)?.sigla === selecionado).map((t) => t.sigla);
  return (
    <MapaDoBrasilSvg
      ufs={tjs.map((t) => t.sigla)}
      ativo={ufsSelecionadas.length > 0 ? ufsSelecionadas : null}
      corDaUf={(uf) => {
        const alvo = alvoDaUf(uf);
        if (!alvo) return corDoStatus("nao_iniciado");
        // No modo TRF a região é o fundo e o status pinta por cima; no modo TJ
        // não há região a mostrar — cada estado é o seu próprio tribunal.
        return modo === "trf" ? corNoModoTrf(progresso, alvo.sigla) : corDoStatus(statusDe(progresso, alvo.sigla));
      }}
      tituloDaUf={(uf) => {
        const alvo = alvoDaUf(uf);
        if (!alvo) return uf;
        return `${alvo.nome_completo} — ${rotuloDoStatus(statusDe(progresso, alvo.sigla))}`;
      }}
      onEscolher={(uf) => {
        const alvo = alvoDaUf(uf);
        if (alvo) onEscolher(alvo.sigla);
      }}
      corDoTraco="var(--cf-mapa-traco)"
    />
  );
}

/**
 * O alternador TJ / TRF — dois botões com `aria-pressed`, sobre navy.
 *
 * O modo viaja na QUERY (`?modo=trf`), não em `useState`: o aluno manda o link
 * dos TRFs para o colega e o botão de voltar desfaz a troca. É a decisão do Mapa
 * de Concursos para os filtros dele.
 */
export function AlternadorDeTipo({
  modo,
  onTrocar,
  contagem,
}: {
  modo: TipoDeTribunal;
  onTrocar: (m: TipoDeTribunal) => void;
  contagem: Record<TipoDeTribunal, number>;
}) {
  const opcoes: { valor: TipoDeTribunal; rotulo: string }[] = [
    { valor: "tj", rotulo: "Tribunais de Justiça" },
    { valor: "trf", rotulo: "Tribunais Regionais Federais" },
  ];
  return (
    <div role="group" aria-label="Tipo de tribunal" className="flex flex-wrap gap-1.5">
      {opcoes.map((o) => (
        <button
          key={o.valor}
          type="button"
          data-modo={o.valor}
          aria-pressed={modo === o.valor}
          onClick={() => onTrocar(o.valor)}
          className={`rounded-full px-3 py-1.5 text-xs font-bold transition ${
            modo === o.valor
              ? "bg-laranja text-azul"
              : "bg-white/10 text-white hover:bg-white/20"
          }`}
        >
          {o.rotulo} <span className="font-semibold opacity-75">{contagem[o.valor]}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * O chip de status, fora do navy. Texto sempre — cor sozinha nunca carrega
 * informação, e os tokens do mapa foram medidos contra o navy, não contra o
 * branco. Aqui a cor é a família semântica da casa, que troca com o tema.
 */
export function ChipDeStatus({ status }: { status: StatusDoCadastro }) {
  const classe =
    status === "concluido"
      ? "bg-aviso-fundo text-aviso-tinta"
      : status === "em_andamento"
        ? "bg-alerta-fundo text-alerta-tinta"
        : "bg-chip text-chip-tinta";
  return (
    <span
      data-status={status}
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${classe}`}
    >
      {rotuloDoStatus(status)}
    </span>
  );
}

/**
 * A lista dos tribunais — e ela não é o "plano B" do mapa.
 *
 * É o caminho do teclado e do leitor de tela (o mapa é `aria-hidden`), o único
 * utilizável no celular, e o mais rápido para quem sabe o tribunal que quer.
 * Cada linha traz o status e a contagem de documentos reunidos: é a lista que a
 * origem não tinha, onde o único número era "N tribunais realizados" na barra
 * lateral.
 */
export function ListaDeTribunais({
  catalogo,
  tribunais,
  progresso,
  base,
}: {
  catalogo: Catalogo;
  tribunais: Tribunal[];
  progresso: Progresso | null;
  base: string;
}) {
  return (
    // `columns-2`, e não `grid-cols-2`: em colunas a primeira metade da lista
    // fica de um lado e a segunda do outro, que é como se lê 27 nomes.
    //
    // Mas só com nome DEMAIS. Com os 6 TRFs em duas colunas, dentro da coluna
    // da direita, cada nome cabia em metade da metade e saía truncado — "Tribunal
    // Regional Federal da 1ª Re…" seis vezes, que é uma lista que não diz nada.
    // O corte é o comprimento do nome contra a largura, e 12 é onde ele fica:
    // acima disso a lista é longa o bastante para valer o corte em duas.
    <ul className={tribunais.length > 12 ? "gap-x-6 sm:columns-2" : ""} data-lista-tribunais>
      {tribunais.map((t) => {
        const s = statusDe(progresso, t.sigla);
        const { marcados, total } = marcadosDo(catalogo, progresso, t.sigla);
        return (
          <li key={t.sigla} className="break-inside-avoid">
            <Link
              to={`${base}/${t.sigla}`}
              data-tribunal={t.sigla}
              className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm text-tinta transition hover:bg-tile focus-visible:bg-tile focus-visible:outline-none"
            >
              {/* O nome SEM o prefixo repetido, e o completo no `title` — o
                  porquê (e o que acontece quando o prefixo não casa) está em
                  `nomeNaLista`. */}
              <span className="min-w-0 flex-1 truncate" title={t.nome_completo}>
                {nomeNaLista(t)}
              </span>
              {s !== "nao_iniciado" && <ChipDeStatus status={s} />}
              <span className="shrink-0 font-mono text-xs text-cinza" title="documentos reunidos">
                {marcados}/{total}
              </span>
              <span className="w-10 shrink-0 text-right font-mono text-xs text-cinza">{t.sigla}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/** O `<select>` do status do cadastro — fora do navy, com a classe da casa. */
export function SeletorDeStatus({
  valor,
  onChange,
  ocupado,
}: {
  valor: StatusDoCadastro;
  onChange: (s: StatusDoCadastro) => void;
  ocupado: boolean;
}) {
  return (
    <label className="block max-w-xs">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.09em] text-cinza">
        Status do meu cadastro
      </span>
      <select
        value={valor}
        disabled={ocupado}
        onChange={(e) => onChange(e.target.value as StatusDoCadastro)}
        data-seletor-status
        className={`${CAMPO} disabled:opacity-60`}
      >
        {STATUS.map((s) => (
          <option key={s.valor} value={s.valor}>
            {s.rotulo}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * O checklist de documentos. Cada tique GRAVA (o pai chama a RPC), e o erro de
 * UM tique fica na linha dele: sem leitura não há tela, sem uma escrita há tela
 * e falta um tique — é a lição do `alternarFeito` do Calendário, onde um tique
 * que não gravou trocava o mês inteiro por uma mensagem de erro.
 */
export function Checklist({
  documentos,
  marcados,
  onMarcar,
  erros,
}: {
  documentos: Documento[];
  marcados: Set<string>;
  onMarcar: (id: string, marcado: boolean) => void;
  /** id do documento → mensagem, para os tiques que não gravaram. */
  erros: Map<string, string>;
}) {
  return (
    <ul className="space-y-1">
      {documentos.map((d) => {
        const marcado = marcados.has(d.id);
        const erro = erros.get(d.id);
        return (
          <li key={d.id}>
            <label className="flex cursor-pointer items-start gap-3 rounded-lg px-2 py-2 transition hover:bg-tile">
              <input
                type="checkbox"
                checked={marcado}
                onChange={(e) => onMarcar(d.id, e.target.checked)}
                data-documento={d.id}
                className="mt-0.5 size-4 shrink-0 accent-laranja"
              />
              <span className="min-w-0 flex-1">
                <span
                  className={`block text-sm leading-snug ${
                    marcado ? "text-cinza line-through" : "text-tinta"
                  }`}
                >
                  {d.texto}
                </span>
                {erro && (
                  <span className="mt-0.5 block text-xs text-erro-tinta" role="alert">
                    Este tique não gravou ({erro}). Os outros continuam aqui — tente de novo.
                  </span>
                )}
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}

/** Um bloco de conteúdo da tela do tribunal — o mesmo desenho dos outros portes. */
export function Bloco({
  titulo,
  descricao,
  children,
  acao,
}: {
  titulo: string;
  descricao?: ReactNode;
  children: ReactNode;
  acao?: ReactNode;
}) {
  const id = `bloco-${titulo
    .toLowerCase()
    .normalize("NFD")
    .replace(/[^a-z]+/g, "-")}`;
  return (
    // `<section role="group" aria-labelledby>` e não `<fieldset><legend>`: a
    // especificação posiciona o legend SOBRE a borda, e com o card arredondado o
    // título sai flutuando fora da caixa (visto na tela em 19/08).
    <section role="group" aria-labelledby={id} className="surface p-5 sm:p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 id={id} className="font-display text-lg font-bold text-titulo">
            {titulo}
          </h3>
          {descricao && <div className="mt-1 text-sm leading-relaxed text-cinza">{descricao}</div>}
        </div>
        {acao}
      </div>
      {children}
    </section>
  );
}

/** Um link externo da ficha: abre em outra aba e DIZ isso ao leitor de tela. */
export function LinkExterno({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 font-semibold text-link hover:underline"
    >
      {children}
      <span aria-hidden="true">↗</span>
      <span className="sr-only">(abre em outra aba)</span>
    </a>
  );
}
